import { indexBy, prop, isEmpty } from 'ramda'
import type { DeepReadonly, ComputedRef, ToRefs } from 'vue'
import type { SortOption, FilterOption, SelectionFilter, RangeFilter, SelectionFilterOption } from '@/entities/filter'

interface SessionStorageParams<T> {
  sessionStorageKey: string
  filterFunc: (option: T) => boolean | undefined
  keyBy: string
  mergeWith: (a: Record<string, T>) => (b: T) => T
}

interface State {
  usingQuerystring: boolean
  sortOptions: SortOption[]
  filterOptions: FilterOption[]
  filters: Record<string, string | number>
}

interface UseFilters extends DeepReadonly<ToRefs<State>> {
  currentSort: ComputedRef<SortOption>
  userSelectedOptions: ComputedRef<FilterOption[]>
  setSort(sort: string): void
  setRange(param: { endpoint: string[]; value: number[] }): void
  setSelectOption(param: { filter: string; option: string; value: boolean }): void
  getRangeValuesFor([minEndpoint, maxEndpoint]: [string, string]): number[] | null[]
  getSelectedOptionsFor(name: string): SelectionFilterOption[]
  resetFiltersAndSortOptions(): void
}

export const filterTypes = Object.freeze({
  SELECTION: 'selection',
  RANGE: 'range',
})

export function useFilters(): UseFilters {
  const route = useRoute()
  const state = reactive<State>({
    filters: {},
    sortOptions: [],
    filterOptions: [],
    usingQuerystring: false,
  })

  const userSelectedOptions = computed(() => state.filterOptions.filter(_isFilterOptionSelected))
  const currentSort = computed(() => {
    const selected = state.sortOptions.filter(option => option.selected)[0]
    return selected ? selected : state.sortOptions[0]
  })

  onMounted(async () => {
    const { sort_options, filters } = await $fetch<{ sort_options: SortOption[]; filters: FilterOption[] }>(
      '/v2/filters',
      useFetchSharedOptions()
    )

    state.sortOptions = _mergeWithSessionStorage<SortOption>({
      sessionStorageKey: 'sortOptions',
      filterFunc: (o: SortOption) => o.selected,
      keyBy: 'endpoint_value',
      mergeWith: (map: Record<string, SortOption>) => (o: SortOption) => {
        if (map[o.endpoint_value]) {
          return { ...o, selected: map[o.endpoint_value].selected }
        }

        return o
      },
    })(sort_options)

    const { queryFilterOptions, shouldUseQuery } = _fromQuerystring({
      query: route.query,
      mergeWith: _mergeFilterOptionsWith,
    })(filters)

    state.usingQuerystring = shouldUseQuery

    if (shouldUseQuery) {
      state.filterOptions = queryFilterOptions
    } else {
      state.filterOptions = _mergeWithSessionStorage<FilterOption>({
        sessionStorageKey: 'filterOptions',
        filterFunc: _isFilterOptionSelected,
        keyBy: 'name',
        mergeWith: _mergeFilterOptionsWith,
      })(filters)
    }
  })

  watch(
    () => state.sortOptions,
    (sortOptions: SortOption[]) => {
      if (!state.usingQuerystring) {
        sessionStorage.setItem('sortOptions', JSON.stringify(sortOptions))
      }
      const selected = sortOptions.filter(option => option.selected === true)
      const sort = selected.length ? selected[0].endpoint_value : sortOptions[0].endpoint_value

      state.filters = {
        ...state.filters,
        sort_by: sort,
      }
    }
  )

  watch(
    () => state.filterOptions,
    (filterOptions: FilterOption[]) => {
      if (!state.usingQuerystring) {
        sessionStorage.setItem('filterOptions', JSON.stringify(filterOptions))
      }
      const selection = filterOptions.filter(option => option.filter_type === filterTypes.SELECTION) as SelectionFilter[]
      selection.map(select => {
        const selectedOptions = select.options.filter(option => option.selected === true)
        if (selectedOptions.length) {
          // filters are passed into API as CSV
          // Need to assign to state.filters directly so reactivity is triggered
          state.filters = { ...state.filters, [select.endpoint_filter]: selectedOptions.map(options => options.filter).join(',') }
        } else {
          state.filters = _removeFilterOption(select.endpoint_filter)
        }
      })

      const ranges = filterOptions.filter(option => option.filter_type === filterTypes.RANGE) as RangeFilter[]
      ranges.map(range => {
        if (range.value) {
          state.filters[range.endpoint_filter_min] = range.value[0]
          state.filters[range.endpoint_filter_max] = range.value[1]
        } else {
          delete state.filters[range.endpoint_filter_min]
          delete state.filters[range.endpoint_filter_max]
        }
      })
    }
  )

  function setSort(sort: string) {
    state.sortOptions = state.sortOptions.map(option => {
      option.selected = option.endpoint_value === sort
      return option
    })
  }

  function setSelectOption({ filter, option, value }: { filter: string; option: string; value: boolean }) {
    state.filterOptions = (state.filterOptions as SelectionFilter[]).map(filterOption => {
      const isSelection = filterOption.endpoint_filter === filter && filterOption.filter_type === filterTypes.SELECTION

      if (isSelection) {
        // assign input selection to state
        filterOption.options = filterOption.options.map(opt => {
          // decorate option with default state
          if (opt.filter === option) {
            opt.selected = value
          }
          return opt
        })
      }

      return filterOption
    })
  }

  function setRange({ endpoint, value }: { endpoint: string[]; value: number[] }) {
    state.filterOptions = state.filterOptions.map(filterOption => {
      const isRange =
        filterOption.filter_type === filterTypes.RANGE &&
        filterOption.endpoint_filter_min === endpoint[0] &&
        filterOption.endpoint_filter_max === endpoint[1]

      if (isRange) {
        filterOption.value = value
      }

      return filterOption
    })
  }

  function getSelectedOptionsFor(name: string): SelectionFilterOption[] {
    const filteredOption = (state.filterOptions as SelectionFilter[]).filter(filter => filter.endpoint_filter === name)[0]
    return filteredOption.options.filter(option => option.selected)
  }

  function getRangeValuesFor([minEndpoint, maxEndpoint]: [string, string]): number[] | null[] {
    const filterOption = (state.filterOptions as RangeFilter[]).filter(
      filterOption =>
        filterOption.filter_type === filterTypes.RANGE &&
        filterOption.endpoint_filter_min === minEndpoint &&
        filterOption.endpoint_filter_max === maxEndpoint
    )
    return filterOption.length && filterOption[0].value ? filterOption[0].value : [null, null]
  }

  function resetFiltersAndSortOptions() {
    state.sortOptions = state.sortOptions.map((option, index) => {
      option.selected = index === 0 ? true : false
      return option
    })

    state.filterOptions = state.filterOptions.map((filter: FilterOption) => {
      if (filter.filter_type === filterTypes.SELECTION) {
        filter.options = filter.options.map(option => {
          delete option.selected
          return option
        })
      }

      if (filter.filter_type === filterTypes.RANGE) {
        delete filter.value
      }

      return filter
    })
  }

  function _removeFilterOption(name: string) {
    const { [name]: value, ...remainder } = state.filters
    return remainder
  }

  function _isFilterOptionSelected(option: FilterOption) {
    if (option.filter_type === filterTypes.SELECTION) {
      return !!option.options.find((o: SelectionFilterOption) => o.selected)
    }

    if (option.filter_type === filterTypes.RANGE) {
      return !!option.value
    }

    return false
  }

  function _fromQuerystring({ query, mergeWith }: { query: any; mergeWith: Function }) {
    return function (fromServer: FilterOption[]) {
      const queryKeys = Object.keys(query)
      const selected = fromServer.filter(f => {
        if (f.filter_type === filterTypes.SELECTION && queryKeys.includes(f.endpoint_filter)) {
          const options = query[f.endpoint_filter].split(',')
          f.options = f.options.map((o: SelectionFilterOption) => (options.includes(o.filter) ? { ...o, selected: true } : o))
          return f
        } else {
          return false
        }
      })
      const selectedMap = indexBy(prop('name'), selected)

      return {
        shouldUseQuery: !isEmpty(selectedMap),
        queryFilterOptions: fromServer.map(mergeWith(selectedMap)) as FilterOption[],
      }
    }
  }

  function _mergeWithSessionStorage<T extends SortOption | FilterOption>({
    sessionStorageKey,
    filterFunc,
    keyBy,
    mergeWith,
  }: SessionStorageParams<T>) {
    return function (fromServer: T[]) {
      const selected = JSON.parse(sessionStorage.getItem(sessionStorageKey) ?? '[]').filter(filterFunc)

      const selectedMap = indexBy<T, string>(prop(keyBy) as () => string, selected)

      return fromServer.map(mergeWith(selectedMap)) as T[]
    }
  }

  function _mergeFilterOptionsWith<T extends FilterOption>(map: Record<string, T>) {
    return function (f: FilterOption) {
      const matching = map[f.name]
      // set default state of select options
      if (f.filter_type === filterTypes.SELECTION) {
        if (matching) {
          f.options = (matching as SelectionFilter).options
        } else {
          f.options.map(o => ({ ...o, selected: false }))
        }
      } else if (f.filter_type === filterTypes.RANGE) {
        if (matching) {
          f.value = (matching as RangeFilter).value
        }
      }

      return f
    }
  }

  return {
    ...toRefs(readonly(state)),
    currentSort,
    userSelectedOptions,
    setSort,
    setRange,
    setSelectOption,
    getRangeValuesFor,
    getSelectedOptionsFor,
    resetFiltersAndSortOptions,
  }
}
